import { useEffect, useMemo, useState, useRef } from 'react';
import { api, darf, datum, euro, menge, type Anmelder, heuteIso} from '../api.ts';
import { Karte, Leer } from './Geruest.tsx';
import {
  Auswahl, Dialog, Feldreihe, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';
import { Kopierbar } from './Kopierbar.tsx';
import { SuchHinweis, useVerzoegert } from './Schluesselsuche.tsx';

/**
 * Die Rechnungen der Subunternehmer, und was das System dagegenhält.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „Was ist mit der Abrechnung und der Intelligenz dahinter? Rechnungen die
 *    rein kommen gegenprüfen vom System?"
 *
 * WAS HIER BIS ZUM 25.08.2026 STAND: eine Liste mit Betrag und Fälligkeit,
 * drei Knöpfen (geprüft, bezahlt, erledigt) und einer Spalte „Befunde", in
 * der bei jeder Zeile eine graue 0 stand. Die Sicht dahinter hatte die Spalte
 * gar nicht mehr. Eine 0 neben einer Rechnung liest sich wie „geprüft, nichts
 * gefunden", und das war sie nie.
 *
 * ES WIRD KEIN PREIS GEGENGERECHNET, und das ist keine Einschränkung, sondern
 * die Bauart. Ohne einen einzigen Einheitspreis lassen sich sieben Fragen
 * stellen: steht die Position in unserem Aufmaß, ist die Menge grösser als
 * die anerkannte, steht dieselbe Position zweimal auf dem Papier, hat die
 * Firma auf diesem Auftrag überhaupt ein Gewerk, ist die Rechnung älter als
 * die Leistung, haben wir selbst schon eingereicht, hängt sie an einem
 * Auftrag. Sechs davon brauchen die POSITIONEN - und genau die gab es
 * bisher nicht.
 */

const GRUPPEN = [
  { wert: 'tiefbau', text: 'Tiefbau' },
  { wert: 'montage', text: 'Montage' },
];

/* ============================================================================
 * DIE FÄLLIGKEIT, FARBIG UND MIT IHREM ABSTAND ZU HEUTE
 * ============================================================================
 * `tage_bis_faellig` rechnet der Server in deutscher Zeit (`heute_de()`),
 * damit der Wechsel um Mitternacht nicht an der Uhr des Telefons hängt.
 * ==========================================================================*/
function Faelligkeit({ r }: { r: any }) {
  if (!r.faellig_am) return <span className="leise">ohne Fälligkeit</span>;
  const t = r.tage_bis_faellig == null ? null : Number(r.tage_bis_faellig);
  const offen = !r.bezahlt_am;
  const farbe = !offen || t == null ? ''
    : t < 0 ? ' f-vorbei' : t <= 7 ? ' f-bald' : ' f-spaeter';
  const abstand = !offen || t == null ? null
    : t < 0 ? `seit ${-t} ${-t === 1 ? 'Tag' : 'Tagen'}`
    : t === 0 ? 'heute'
    : t === 1 ? 'morgen'
    : `in ${t} Tagen`;
  return (
    <>
      <span className={'faellig' + farbe}>
        {datum(r.faellig_am)}
        {abstand && <span className="faellig-abstand">, {abstand}</span>}
      </span>
      {r.faellig_wie && (
        <div className="klein leise">{r.faellig_wie}</div>
      )}
    </>
  );
}

/** KLS-ID und SM-Nummer nebeneinander, jede mit einem Klick kopierbar. */
/* Der Titel eines Nachweises aus dem Dateinamen (.522): „10030364 Privatgrund …" wird „Privatgrund (10030364)". */
function nachweisTitel(name: string): string {
  const m = String(name ?? '').replace(/\.pdf$/i, '').match(/^(?:.*?)(\d{8})[\s_-]*(.*)$/);
  if (!m) return String(name ?? '').replace(/\.pdf$/i, '');
  const rest = (m[2] ?? '').replace(/[_]+/g, ' ').replace(/\s*\d{4}-\d{2}-\d{2}.*$/, '').trim();
  return rest ? `${rest} (${m[1]})` : m[1]!;
}

export function NummernKopieren({ kls, sm }: { kls?: string | null; sm?: string | null }) {
  if (!kls && !sm) return null;
  return (
    <div className="nummernkopie">
      {kls && <span><span className="leise">KLS </span><Kopierbar wert={kls} was="KLS-ID" klein /></span>}
      {sm && <span><span className="leise">SM </span><Kopierbar wert={sm} was="SM-Nummer" klein /></span>}
    </div>
  );
}

const BEFUNDWORT: Record<string, string> = {
  ohne_auftrag: 'ohne Auftrag',
  nicht_zustaendig: 'nicht zuständig',
  vor_der_leistung: 'vor der Leistung',
  nicht_erfasst: 'nicht im Aufmaß',
  mehr_als_erfasst: 'mehr als unser Aufmaß',
  doppelt: 'doppelt berechnet',
  nicht_eingereicht: 'noch nicht eingereicht',
  /* DIE NUMMER, DIE DAS PAPIER ÜBERHAUPT ERST BENENNT (Migration 291).
     ORIGINALTEXT MARVIN (22.09.2026)
       „warum fehlt hier die Rechnungsnummer. die rechnungen sind alle
        immer die gleichen" */
  ohne_nummer: 'ohne Nummer',
  nummer_doppelt: 'Nummer doppelt',
  zweite_fassung: 'zweite Fassung',
  /* DAS GEWERK DER RECHNUNG GEGEN DIE STAMMDATEN DER FIRMA (Migration 293).
     ORIGINALTEXT MARVIN (22.09.2026)
       „es sind immernoch die gleichen fehler mit der bezeichnung
        Tiefbau/montage" */
  gewerk_passt_nicht: 'Gewerk passt nicht',
  /* GEGEN DEN EINGEREICHTEN LEB (Migration 274).
     „wir legen drauf" und „bleibt bei uns" sind Marvins eigene Worte vom
     21.09.2026, und sie stehen hier, weil sie in einem Wort sagen, worum es
     geht. „Mehr als eingereicht" sagt, was gemessen wurde; „wir legen drauf"
     sagt, was es kostet. */
  mehr_als_eingereicht: 'mehr als an die Telekom gemeldet, wir legen drauf',
  weniger_als_eingereicht: 'weniger als an die Telekom gemeldet, bleibt bei uns',
  nie_eingereicht: 'nie an die Telekom gemeldet, kein Geld dafür',
  /* DIE SAMMELRECHNUNG (Migration 305). Ein weicher Befund: sie ist kein
     Widerspruch, sondern eine Form, die die Prüfung je Auftrag noch nicht
     gegenhält. */
  sammelrechnung: 'Sammelrechnung',
  /* GEGEN DIE GUTSCHRIFT DER TELEKOM (Migration 323): was sie wirklich
     bezahlt hat. */
  mehr_als_gutgeschrieben: 'mehr als die Telekom gutgeschrieben hat, wir legen drauf',
  nicht_gutgeschrieben: 'von der Telekom nicht gutgeschrieben',
};


/* ============================================================================
 * DAS ERGEBNIS DER PRÜFUNG EINER RECHNUNG IN EINEM WORT UND EINER FARBE (.215)
 * ============================================================================
 * Dasselbe an 3 Stellen: in der Liste, an der Freigabe und unter „Neue
 * Gutschriften". Rot Widerspruch, orange zum Ansehen, grau ohne erfasste
 * Positionen (dann lässt sich nichts gegenhalten), grün stimmt.
 * `r` ist eine Zeile der Liste oder `rechnung_lage()` aus der Datenbank.
 * ==========================================================================*/
export function rechnungsLage(r: any): { art: string; farbe: string; text: string } {
  const hart = Number(r.befunde_hart ?? r.hart ?? 0);
  const alle = Number(r.befunde ?? 0);
  const pos = Number(r.positionszeilen ?? r.positionen ?? 0);
  if (hart > 0) {
    return { art: 'rot', farbe: 'z-ueberfaellig',
             text: (BEFUNDWORT[r.befund_art] ?? 'Widerspruch') + (hart > 1 ? ` +${hart - 1}` : '') };
  }
  if (alle > 0) {
    return { art: 'orange', farbe: 'z-neu',
             text: (BEFUNDWORT[r.hinweis_art] ?? 'zum Ansehen') + (alle > 1 ? ` +${alle - 1}` : '') };
  }
  if (pos === 0) {
    return { art: 'grau', farbe: 'z-laufend',
             text: 'keine Positionen' + (POS_GRUND[r.positionen_grund] ? `: ${POS_GRUND[r.positionen_grund]}` : '') };
  }
  /* PER TEXTERKENNUNG GELESEN (.216): ohne Widerspruch, aber noch nicht
     gegen das Papier gesehen. Grün wäre eine Zusage, die niemand gemacht hat. */
  if (r.positionen_ocr) return { art: 'orange', farbe: 'z-neu', text: 'per Texterkennung, bitte gegen PDF prüfen' };
  return { art: 'gruen', farbe: 'z-erledigt', text: 'stimmt' };
}

/** Die Rechnung einer Firma an einer Freigabezeile: Nummer, Ergebnis, ein Klick ins Prüffenster. */
export function RechnungChip({ r, pruefen }: { r: any; pruefen?: (id: string) => void }) {
  if (!r) return null;
  const l = rechnungsLage(r);
  return (
    <button type="button" className={'marke-zustand rk-chip ' + l.farbe}
            title={(r.befund_text ?? '') + (pruefen ? ', Klick öffnet das Prüffenster' : '')}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); pruefen?.(r.id); }}>
      Rechnung {r.nummer ?? r.rechnungsnummer ?? ''}: {l.text}
    </button>
  );
}

/** Warum eine Rechnung keine Positionen hat, in einem Wort (Migration 324). */
const POS_GRUND: Record<string, string> = {
  ohne_pdf: 'kein PDF', ohne_text: 'Scan ohne Text', fremde_nummern: 'eigene Nummern der Firma',
  unerkannte_zeilen: 'Zeilen ohne Nummer', keine_tabelle: 'keine Positionszeile erkannt',
  noch_nicht_gelesen: 'noch nicht gelesen',
};
const POS_GRUND_LANG: Record<string, string> = {
  ohne_pdf: 'Kein PDF an der Rechnung: sie kam, bevor das Papier aufgehoben wurde. Positionen von Hand erfassen oder „Rechnungen nachholen" laufen lassen.',
  ohne_text: 'Scan ohne Text, und auch die Texterkennung fand nichts Lesbares. Positionen von Hand erfassen.',
  fremde_nummern: 'Die Firma schreibt eigene Nummern. Unten einmal unserer Leistung zuordnen.',
  unerkannte_zeilen: 'Zeilen mit Menge und Preis, aber ohne Nummer. Unten einmal unserer Leistung zuordnen.',
  keine_tabelle: 'Keine Zeile sieht wie eine Position aus. Das PDF ansehen und von Hand erfassen.',
  noch_nicht_gelesen: 'Noch nicht mit dem neuen Weg gelesen: „Positionen nachlesen" drücken.',
};

/* ============================================================================
 * RECHNUNGEN OHNE POSITIONEN: WARUM, UND DIE ZUORDNUNG (.216)
 * ============================================================================
 * ORIGINALTEXT MARVIN (23.09.2026)
 *   „ja prüfe das gegen, denn wir brauchen valide verlässliche daten und
 *    informationen."
 * Oben die Gründe mit Anzahl. Darunter die Nummern und Zeilen, die Firmen
 * statt unserer schreiben, die häufigsten zuerst: 1 Zuordnung macht alle
 * Rechnungen dieser Firma lesbar, auch die künftigen.
 * ==========================================================================*/
function OhnePositionen({ fertig }: { fertig: () => void }) {
  const [d, setD] = useState<any>(null);
  const [offen, setOffen] = useState(false);
  const [wahl, setWahl] = useState<Record<string, string>>({});
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  async function laden() { setD(await api.ohnePositionen()); }
  useEffect(() => { laden().catch(() => {}); }, []);
  useFrisch(() => laden().catch(() => {}));
  if (!d) return null;
  const summe = d.gruende.reduce((s: number, g: any) => s + g.n, 0);
  if (!summe && !d.zuordnungen.length) return null;
  const k = (f: any) => `${f.subunternehmer_id}|${f.nummer}`;
  async function zuordnen(f: any) {
    const oz = wahl[k(f)];
    if (!oz) return;
    setLaeuft(true); setHinweis(null);
    try { const e = await api.fremdnummerZuordnen(f.subunternehmer_id, f.nummer, oz); setHinweis(e.klartext); await laden(); fertig(); }
    catch (x: any) { setHinweis(String(x?.message ?? x)); }
    finally { setLaeuft(false); }
  }
  async function nachlesen() {
    setLaeuft(true); setHinweis('Liest alle Rechnungen ohne Positionen neu …');
    try { const e = await api.positionenNachlesen(); setHinweis(e.klartext); await laden(); fertig(); }
    catch (x: any) { setHinweis(String(x?.message ?? x)); }
    finally { setLaeuft(false); }
  }
  return (
    <div className="karte op-karte">
      <div className="listenkopf">
        <b>Rechnungen ohne Positionen ({summe})</b>
        <div className="knopfreihe">
          <button className="knopf klein" disabled={laeuft} onClick={nachlesen}>Positionen nachlesen</button>
          <button className="knopf klein" onClick={() => setOffen(!offen)}>
            {offen ? 'zuklappen' : 'warum, und was tun'}
          </button>
        </div>
      </div>
      <div className="op-gruende">
        {d.gruende.map((g: any) => (
          <span key={g.grund} className="marke-zustand z-laufend" title={POS_GRUND_LANG[g.grund] ?? ''}>
            {POS_GRUND[g.grund] ?? g.grund}: {g.n}
          </span>
        ))}
      </div>
      {hinweis && <div className="hinweis">{hinweis}</div>}
      {offen && (
        <>
          <ul className="klein op-erklaerung">
            {d.gruende.map((g: any) => (
              <li key={g.grund}><b>{POS_GRUND[g.grund] ?? g.grund}</b>: {POS_GRUND_LANG[g.grund] ?? ''}</li>
            ))}
          </ul>
          {d.fremd.length > 0 && (
            <table className="tabelle op-tabelle">
              <thead><tr><th>Firma</th><th>schreibt</th><th className="zahl">Rechnungen</th><th>ist bei uns</th><th></th></tr></thead>
              <tbody>
                {d.fremd.map((f: any) => (
                  <tr key={k(f)}>
                    <td>{f.firma}</td>
                    <td><b>{f.nummer}</b>{f.text ? <span className="leise">, {f.text}</span> : null}</td>
                    <td className="zahl">{f.rechnungen}</td>
                    <td>
                      <select value={wahl[k(f)] ?? ''} onChange={(e) => setWahl({ ...wahl, [k(f)]: e.target.value })}>
                        <option value="">Leistung wählen …</option>
                        {d.leistungen.map((l: any) => (
                          <option key={l.oz} value={l.oz}>{l.oz} {l.text}{l.eigene_position ? ' (eigene)' : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td><button className="knopf klein haupt" disabled={!wahl[k(f)] || laeuft} onClick={() => zuordnen(f)}>zuordnen</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {d.zuordnungen.length > 0 && (
            <div className="klein" style={{ marginTop: 8 }}>
              <b>Schon zugeordnet:</b>{' '}
              {d.zuordnungen.map((z: any) => (
                <span key={z.id} className="op-zuordnung">
                  {z.firma}: „{z.nummer}" = {z.oz} {z.text}
                  <button className="alslink" onClick={async () => { await api.fremdnummerLoeschen(z.id); await laden(); }}>lösen</button>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Überfällig, diese Woche, später, ohne Fälligkeit - in dieser Reihenfolge. */
function rechnungsGruppen(zeilen: any[]): [string, any[]][] {
  const g: Record<string, any[]> = { a: [], b: [], c: [], d: [] };
  for (const r of zeilen) {
    const t = r.tage_bis_faellig == null ? null : Number(r.tage_bis_faellig);
    if (!r.faellig_am || t == null) g.d.push(r);
    else if (t < 0 && !r.bezahlt_am) g.a.push(r);
    else if (t <= 7) g.b.push(r);
    else g.c.push(r);
  }
  return ([['Überfällig', g.a], ['Fällig in den nächsten 7 Tagen', g.b],
           ['Später fällig', g.c], ['Ohne Fälligkeit', g.d]] as [string, any[]][])
    .filter(([, z]) => z.length);
}

/** Eine Zeile im Positionsformular. */
interface Zeile { oz: string; bezeichnung: string; menge: string; einheit: string }

const LEERZEILE: Zeile = { oz: '', bezeichnung: '', menge: '', einheit: '' };


// ============================================================================
// Das Prüffenster
// ============================================================================

/* ============================================================================
 * KORREKTUR AN DIE FIRMA
 * ============================================================================
 * ORIGINALTEXT MARVIN (23.09.2026)
 *   „ich muss nich sein aufmaß bzw seine positionen in meins übernehmen, ich
 *    muss ihm, wenn sie falsch sind, die richtigen infos und postionen geben."
 *   „sie sollen ihre rechnung schreiben, und danach prüfe ich und ggf. teile
 *    ich ihnen dann veränderungen mit."
 *
 * JE POSITION DER RECHNUNG EINE ZEILE, gegen das Aufmaß:
 *   rot     mehr berechnet als anerkannt, oder gar nicht im Aufmaß
 *           -> angehakt, richtig = anerkannt (0, wenn nicht im Aufmaß)
 *   orange  nicht mehr als anerkannt, aber mehr als eingereicht
 *           -> nicht angehakt; Marvin entscheidet
 *   blau    weniger berechnet, oder im Aufmaß und nicht auf der Rechnung
 *           -> NUR FÜR IHN, ohne Haken: das geht nie von selbst hinaus
 *
 * Hinaus geht NUR, was angehakt ist, mit der Menge, die im Feld steht. Die
 * Vorschau zeigt den Text, den die Firma bekommt.
 * ==========================================================================*/
import { GEBUEHR_OZ, gegenpruefungZeilen, korrekturText, korrekturZeile } from './gegenpruefung.ts';
import { fragen, eingeben } from './rueckfrage.tsx';

/**
 * GEGENPRÜFUNG (.358, EIN BLOCK SEIT .391).
 * ============================================================================
 * ORIGINALTEXT MARVIN (14.10.2026)
 *   „Und dann, wenn die Rechnung da ist, gegenprüfen. Stimmt das, was auf der
 *    Gutschrift steht? Hat er das gemacht?"
 * ORIGINALTEXT MARVIN (17.10.2026)
 *   „Wir brauchen das nicht doppelt und dreifach, das macht alles nur länger
 *    und größer. Das reicht oben in dem Block und dann aber muss das Ergebnis
 *    hinten stimmen ... vor allen Dingen alles dann in grün schreiben, würde
 *    alles passen, das ist ja Blödsinn. ... es muss erkennbar sein, was haben
 *    wir geschickt, was wurde genehmigt, was hat der Subunternehmer
 *    geschickt, dass es passt."
 *
 * EINE TABELLE, JE POSITION EINE ZEILE: Aufmaß der Firma, was wir der
 * Telekom geschickt haben (LEB), was sie genehmigt hat (Gutschrift), was wir
 * angefordert haben, was die Firma berechnet. Das Ergebnis misst die
 * RECHNUNG AN DER GUTSCHRIFT, sobald eine mit Positionen da ist; vorher an
 * dem, was angefordert war. Grün heisst nur: Rechnung = Gutschrift.
 * Rot: mehr berechnet, als die Telekom gutschreibt (kostet Geld), dort steht
 * die Entscheidung gleich in der Zeile. Blau: weniger berechnet, nur für
 * dich. Der zweite Block („Positionsvergleich") ist weg.
 */
function Gegenpruefung({ d, id, fertig, darfPruefen }: { d: any; id: string; fertig: () => void; darfPruefen: boolean }) {
  const r = d.rechnung;
  const zeilen = useMemo(() => gegenpruefungZeilen(d), [d]);
  const zahl = (x: number | null, e: string | null) => x === null ? '–' : `${String(Math.round(x * 1000) / 1000).replace('.', ',')}${e ? ' ' + e : ''}`;
  const mitRechnung = zeilen.filter((z) => (z.pm ?? 0) > 0);
  const passen = mitRechnung.filter((z) => z.lage === 'gruen');
  const rot = zeilen.filter((z) => z.lage === 'rot');
  const blau = zeilen.filter((z) => z.lage === 'blau');
  const hatGutschrift = (d.gutgeschrieben ?? []).length > 0;
  const massstab = zeilen[0]?.massstab ?? 'aufmass';
  const montage = d.anforderung?.gewerk_gruppe === 'montage';

  /* DIE ENTSCHEIDUNG JE ROTER ZEILE, in der Zeile selbst. Vorbelegt mit dem,
     was die Telekom gutgeschrieben hat. */
  const [wahl, setWahl] = useState<Record<string, { an: boolean; richtig: string }>>({});
  const [satz, setSatz] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  /* JEDE ZEILE IST WÄHLBAR (.395). Marvin, 17.10.2026: „es muss ja dann die
     andere Position hinzugenommen werden. Es wird eine gestrichen und dazu
     soll eine andere Position dazugenommen werden ... muss ich da einzeln
     selektieren und deselektieren." Vorgewählt: rot (zu viel, nicht
     gutgeschrieben: streichen oder kürzen) und blau „nicht berechnet"
     (aufnehmen). Alles andere lässt sich dazunehmen, mit eigener Menge. */
  useEffect(() => {
    const w: Record<string, { an: boolean; richtig: string }> = {};
    for (const k of zeilen) w[k.key] = { an: !!k.vorwahl, richtig: String(k.soll).replace('.', ',') };
    setWahl(w);
  }, [zeilen]);
  const angehakt = zeilen.filter((k) => wahl[k.key]?.an);
  const richtigZahl = (k: any) => Number(String(wahl[k.key]?.richtig ?? '0').replace(',', '.')) || 0;
  const vorschau = korrekturText({
    nr: r?.rechnungsnummer ? `Rechnung ${r.rechnungsnummer}` : 'Deine Rechnung',
    adresse: r?.strasse ? [`${r.strasse} ${r.hausnummer ?? ''}`.trim(), [r.plz, r.ort_text].filter(Boolean).join(' ')].filter(Boolean).join(', ') : null,
    kenn: [r?.kls_id ? `KLS ${r.kls_id}` : null, r?.sm_nummer ? `SM ${r.sm_nummer}` : null].filter(Boolean).length
      ? ` (${[r?.kls_id ? `KLS ${r.kls_id}` : null, r?.sm_nummer ? `SM ${r.sm_nummer}` : null].filter(Boolean).join(', ')})` : '',
    punkte: angehakt.map((k) => korrekturZeile({ oz: k.oz, bezeichnung: k.name, einheit: k.einheit, menge_rechnung: k.pm, menge_richtig: richtigZahl(k) })),
    satz: satz.trim() || null,
  });
  async function schicken() {
    if (!angehakt.length) return;
    if (!await fragen(`Korrektur an ${r.firma ?? 'die Firma'} schicken?\n\n${vorschau}`)) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.rechnungKorrektur(id, angehakt.map((k) => ({ oz: k.oz, bezeichnung: k.name, einheit: k.einheit, menge_rechnung: k.pm, menge_richtig: richtigZahl(k) })), satz.trim() || null);
      setHinweis(e.klartext); setSatz(''); fertig();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }
  const offen = (d.korrekturen ?? []).filter((k: any) => !k.erledigt_am);

  if (!r?.auftrag_id) {
    return <div className="karte gegenpruefung" style={{ marginBottom: 14 }}><div className="urteil u-grau"><b>Ohne Auftrag gibt es nichts zu vergleichen.</b><span className="klein">Den Beleg als PDF einlesen: dann sucht die Plattform den Auftrag und liest die Positionen.</span></div></div>;
  }
  if (!zeilen.length) {
    return <div className="karte gegenpruefung" style={{ marginBottom: 14 }}><div className="urteil u-grau"><b>Auf der Rechnung sind keine Positionen erfasst.</b><span className="klein">Beleg einlesen oder die Positionen unten von Hand erfassen.</span></div></div>;
  }
  /* DAS URTEIL IN EINER ZEILE. */
  const urteil = rot.length
    ? { farbe: 'rot', text: `Nicht so durchlassen: ${rot.length} ${rot.length === 1 ? 'Position' : 'Positionen'} über dem, was ${massstab === 'gutschrift' ? 'die Telekom gutgeschrieben hat' : massstab === 'angefordert' ? 'angefordert war' : 'im Aufmaß steht'}` }
    : { farbe: 'gruen', text: massstab === 'gutschrift' ? `Alle ${passen.length} berechneten Positionen passen zur Gutschrift` : massstab === 'angefordert' ? `Alle ${passen.length} berechneten Positionen passen zur Anforderung (${montage ? 'Montage: Maßstab ist das Angeforderte, nicht die Gutschrift' : 'Gutschrift ohne Positionen'})` : `Alle ${passen.length} berechneten Positionen passen zum Aufmaß (noch nichts angefordert)` };

  return (
    <div className="karte gegenpruefung" style={{ marginBottom: 14 }}>
      <div className="reihe" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <b>Gegenprüfung: geschickt, genehmigt, berechnet</b>
        <span className="klein leise">
          {d.anforderung ? `angefordert ${datum(d.anforderung.freigegeben_am)}${d.anforderung.gutschrift_nummer ? `, Gutschrift ${d.anforderung.gutschrift_nummer}` : ''}${(d.anforderung.abweichungen ?? []).length ? `, ${d.anforderung.abweichungen.length} Änderungen genannt` : ', wie eingereicht'}${(d.anforderung.nicht_positionen ?? []).length ? `, ${d.anforderung.nicht_positionen.length} abgesagt` : ''}` : 'nie angefordert'}
          {offen.length > 0 && <>, <span className="marke-zustand z-neu">Korrektur verlangt am {datum(offen[0].gesendet_am)}</span></>}
        </span>
      </div>
      <div className={'urteil u-' + urteil.farbe}><b>{urteil.text}</b>{blau.length > 0 && <span className="klein">{blau.length} weniger oder nicht berechnet: steht nur für dich da.</span>}</div>
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis">{hinweis}</div>}
      {/* IN KORREKTUR, ALSO NICHT IN DER ZAHLUNG (.538). Marvin, 28.10.2026, RE1338:
          „Die habe ich gerade quittiert. Finde sie aber nicht zum Bezahlen ... Wo
          ist diese Rechnung jetzt?" Hier steht es, mit dem Weg zurück. */}
      {offen.length > 0 && (
        <div className="hinweis warnung" style={{ marginTop: 8 }}>
          <b>Steht nicht in der Zahlung:</b> am {datum(offen[0].gesendet_am)} wurde bei der Firma eine Korrektur verlangt ({(offen[0].positionen ?? []).length} {(offen[0].positionen ?? []).length === 1 ? 'Position' : 'Positionen'}).
          Die Rechnung kommt in die Zahlung, sobald die neue Rechnung der Firma da ist, oder sobald du sie als geprüft abhakst oder die Korrektur zurücknimmst (die Rechnung gilt dann so).
          {/* .540: die Nachricht an die Firma ist freiwillig. Marvin: „Das kannst du schon mal optional machen, dass die kontaktiert werden." */}
          <div className="reihe" style={{ marginTop: 6, gap: 6, flexWrap: 'wrap' }}>
            {([false, true] as const).map((mit) => (
              <button key={String(mit)} type="button" className="knopf klein" disabled={laeuft}
                      onClick={async () => {
                        if (!await fragen(`Korrektur vom ${datum(offen[0].gesendet_am)} zurücknehmen? Die Rechnung ${r?.rechnungsnummer ?? ''} gilt wie eingereicht und steht wieder in der Zahlung.${mit ? ' Die Firma bekommt es in ihre Freigaben und per Telegram.' : ' Die Firma wird nicht benachrichtigt.'}`)) return;
                        setLaeuft(true); setFehler(null); setHinweis(null);
                        try { const e = await api.rechnungKorrekturZuruecknehmen(id, offen[0].id, mit); setHinweis(e.klartext); fertig(); }
                        catch (f) { setFehler(ausFehler(f)); }
                        finally { setLaeuft(false); }
                      }}>{mit ? 'Zurücknehmen und Firma informieren' : 'Korrektur zurücknehmen, ohne Nachricht'}</button>
            ))}
          </div>
        </div>
      )}
      {(d.korrekturen ?? []).map((k: any) => (
        <div key={k.id} className="klein leise" style={{ marginTop: 6 }}>
          Korrektur vom {datum(k.gesendet_am)}: {(k.positionen ?? []).length} Positionen
          {k.zurueckgenommen_am ? `, zurückgenommen am ${datum(k.zurueckgenommen_am)} (Rechnung gilt wie eingereicht)` : k.erledigt_am ? (k.neue_rechnung_id && k.neue_rechnung_id === r?.id ? `, umgesetzt mit dieser Rechnung, abgeglichen am ${datum(k.erledigt_am)}` : `, ersetzt durch ${k.neue_nummer ?? 'eine neue Rechnung'} am ${datum(k.erledigt_am)}`) : ', wartet auf die neue Rechnung'}
        </div>
      ))}
      <div className="gp-tabelle">
        {/* VIER ZAHLEN STATT FÜNF (.408). ORIGINALTEXT MARVIN (19.10.2026):
            „Wir haben das Aufmaß der Firma, dann haben wir das, was wir
            geschickt haben, das, was wir genehmigt haben und dann ist auch
            gut. Angefordert und berechnet sehe ich jetzt keinen Sinn drin."
            „Angefordert" ist weg (das Angeforderte ist die Gutschrift, nur
            noch als Maßstab, wenn keine Gutschrift mit Positionen da ist).
            Die Menge auf der Rechnung bleibt, aber unter ihrem Namen: ohne
            sie hätte das Ergebnis keine Grundlage, die man sieht. */}
        <div className="gp-zeile gp-kopf"><span>Position</span><span className="r">Aufmaß Firma</span><span className="r">Geschickt (LEB)</span><span className="r">Genehmigt (Gutschrift)</span><span className="r">Auf der Rechnung</span><span>Ergebnis</span></div>
        {zeilen.map((z) => (
          <div key={z.key} className={`gp-zeile l-${z.lage}`}>
            <span>{z.oz}{z.name ? ` ${z.name}` : ''}{z.abgesagt > 0 && <span className="klein gp-abgesagt" title="Bei der Freigabe der Firma gesagt: nicht deine Position">, abgesagt: {zahl(z.abgesagt, z.einheit)}</span>}</span>
            <span className="r leise" data-titel="Aufmaß Firma">{zahl(z.am, z.einheit)}</span>
            <span className="r" data-titel="Geschickt (LEB)">{zahl(z.em, z.einheit)}</span>
            <span className={`r ${z.massstab === 'gutschrift' ? 'gp-soll' : ''}`} data-titel="Genehmigt (Gutschrift)"
                  title={z.massstab === 'angefordert' ? `Keine Gutschrift mit Positionen; Maßstab ist das Angeforderte: ${zahl(z.angefordert, z.einheit)}` : undefined}>{zahl(z.gs, z.einheit)}</span>
            <span className="r gp-rechnung" data-titel="Auf der Rechnung">{zahl(z.pm, z.einheit)}</span>
            <span className={`gp-wort ${z.diff > 0.005 ? 'gp-mehr' : z.diff < -0.005 ? 'gp-weniger' : ''}`} title={z.wort}>
              <b className="gp-kurz">{z.kurz === 'passt' ? '✓ passt' : z.kurz}</b>{z.kurzWort && <span className="klein"> {z.kurzWort}</span>}
            </span>
            {/* DIE ENTSCHEIDUNG IN EINER EIGENEN ZEILE DARUNTER (.393), nicht in
                der Ergebnisspalte: dort stapelte sie sich und machte die Zeile
                dreimal so hoch (Bild RE26042). */}
            {darfPruefen && r.subunternehmer_id && !r.geklaert_am && (
              <div className={`gp-entscheidung ${wahl[z.key]?.an ? 'an' : ''}`}>
                <label className="gp-haken">
                  <input type="checkbox" checked={!!wahl[z.key]?.an}
                         onChange={(e) => setWahl({ ...wahl, [z.key]: { ...(wahl[z.key] ?? { richtig: String(z.soll) }), an: e.target.checked } })} />
                  {wahl[z.key]?.an
                    ? (richtigZahl(z) === 0 ? 'streichen' : z.pm === null || z.pm === 0 ? 'aufnehmen' : 'ändern')
                    : z.lage === 'rot' ? 'so akzeptieren' : 'in die Korrektur'}
                </label>
                {wahl[z.key]?.an && (
                  <label className="korrektur-richtig">richtig
                    <input value={wahl[z.key].richtig} inputMode="decimal" onChange={(e) => setWahl({ ...wahl, [z.key]: { ...wahl[z.key], richtig: e.target.value } })} />
                    {z.einheit ?? ''}
                  </label>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* IN EURO (.470). ORIGINALTEXT MARVIN (23.10.2026, RE26035): „Kannst du
          das bitte prüfen anhand von Beträgen, wo du dann sagst, da ist das
          Minus und da ist das Plus, mit einer Summe, damit ich das gegenprüfen
          kann". Je Zeile: Menge × Preis der Firma auf der Rechnung, nach
          Maßstab (Gutschrift) und mit der gewählten Korrektur; darunter die
          Summen. Nur intern, nur mit abrechnung.geld. */}
      {d.darfGeld && d.preise && (() => {
        const preis = (oz: string) => (d.preise[oz] ?? null) as number | null;
        const eur = (x: number) => x.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
        const vz = (x: number) => (x > 0.004 ? '+' : '') + eur(x);
        const rows = zeilen.map((z) => {
          const p = preis(z.oz);
          const ist = z.pm ?? 0;
          const nachKorrektur = wahl[z.key]?.an ? richtigZahl(z) : ist;
          return { z, p, ist: p === null ? null : ist * p, soll: p === null ? null : z.soll * p,
                   korr: p === null ? null : nachKorrektur * p, aenderung: p === null ? null : (nachKorrektur - ist) * p };
        }).filter((x) => (x.z.pm ?? 0) > 0 || x.z.soll > 0 || wahl[x.z.key]?.an);
        const sum = (k: 'ist' | 'soll' | 'korr' | 'aenderung') => rows.reduce((a, x) => a + (x[k] ?? 0), 0);
        const ohnePreis = rows.filter((x) => x.p === null);
        const betragRechnung = r?.betrag != null ? Number(r.betrag) : null;
        return (
          <div className="gp-euro">
            <div className="gp-euro-zeile gp-kopf"><span>Position (Preis der Firma)</span><span className="r">Rechnung</span><span className="r">{massstab === 'gutschrift' ? 'laut Gutschrift' : massstab === 'angefordert' ? 'laut Anforderung' : 'laut Aufmaß'}</span><span className="r">Unterschied</span><span className="r">Korrektur wirkt</span></div>
            {rows.map(({ z, p, ist, soll, aenderung }) => {
              const unter = ist === null || soll === null ? null : soll - ist;
              return (
                <div key={z.key} className="gp-euro-zeile">
                  <span>{z.oz}{z.name ? ` ${z.name}` : ''}<span className="klein leise">{p === null ? ', kein Preis hinterlegt' : `, ${eur(p)}/${z.einheit ?? 'Einh.'}`}</span></span>
                  <span className="r">{ist === null ? '–' : eur(ist)}</span>
                  <span className="r">{soll === null ? '–' : eur(soll)}</span>
                  <span className={'r ' + (unter === null ? '' : unter < -0.004 ? 'gp-minus' : unter > 0.004 ? 'gp-plus' : '')}>{unter === null ? '–' : Math.abs(unter) < 0.005 ? '0,00 €' : vz(unter)}</span>
                  <span className={'r ' + (aenderung === null ? '' : aenderung < -0.004 ? 'gp-minus' : aenderung > 0.004 ? 'gp-plus' : '')}>{aenderung === null || Math.abs(aenderung) < 0.005 ? (wahl[z.key]?.an ? '0,00 €' : '') : vz(aenderung)}</span>
                </div>
              );
            })}
            <div className="gp-euro-zeile gp-summe">
              <span><b>Summe</b>{betragRechnung !== null && Math.abs(betragRechnung - sum('ist')) > 0.01 && <span className="klein leise">, Rechnungsbetrag {eur(betragRechnung)} (netto/brutto oder Positionen ohne Preis)</span>}</span>
              <span className="r"><b>{eur(sum('ist'))}</b></span>
              <span className="r"><b>{eur(sum('soll'))}</b></span>
              <span className={'r ' + (sum('soll') - sum('ist') < -0.004 ? 'gp-minus' : sum('soll') - sum('ist') > 0.004 ? 'gp-plus' : '')}><b>{vz(sum('soll') - sum('ist'))}</b></span>
              <span className={'r ' + (sum('aenderung') < -0.004 ? 'gp-minus' : sum('aenderung') > 0.004 ? 'gp-plus' : '')}><b>{angehakt.length ? vz(sum('aenderung')) : ''}</b></span>
            </div>
            {angehakt.length > 0 && (
              <div className="klein" style={{ marginTop: 6 }}>
                Mit der gewählten Korrektur wird die Rechnung <b>{eur(sum('korr'))}</b> statt {eur(sum('ist'))}
                {' '}({vz(sum('aenderung'))}). {Math.abs(sum('korr') - sum('soll')) < 0.01
                  ? <span className="gp-plus">Das ist genau der Wert laut {massstab === 'gutschrift' ? 'Gutschrift' : 'Maßstab'}.</span>
                  : <span className="gp-minus">Zum Wert laut {massstab === 'gutschrift' ? 'Gutschrift' : 'Maßstab'} bleiben {vz(sum('soll') - sum('korr'))}.</span>}
              </div>
            )}
            {ohnePreis.length > 0 && <div className="klein leise" style={{ marginTop: 4 }}>Ohne Preis der Firma (nicht mitgerechnet): {ohnePreis.map((x) => x.z.oz).join(', ')}. Preise stehen in den Stammdaten der Firma.</div>}
          </div>
        );
      })()}
      {!hatGutschrift && <p className="klein leise" style={{ margin: '6px 0 0' }}>Die Gutschrift der Telekom hat noch keine gelesenen Positionen: gemessen wird an dem, was angefordert war.</p>}
      {!(d.eingereicht ?? []).length && <p className="klein leise" style={{ margin: '6px 0 0' }}>Spalte „Geschickt (LEB)" leer: das LEB dieses Auftrags hat in der Plattform keine Positionen (Übernahme aus dem alten Blatt oder noch nicht erzeugt).</p>}
      {darfPruefen && r.subunternehmer_id && angehakt.length > 0 && (
        <div className="korrekturfuss">
          <Text name="korrektursatz" titel="Satz an die Firma (freiwillig)" wert={satz} setze={setSatz} mehrzeilig fehler={fehler} />
          {/* DIE VORSCHAU IST OFFEN (.395). Marvin: „die Vorschau muss hier auch aufgeklappt sein." */}
          <div className="pushvorschau" style={{ marginTop: 10 }}>
            <div className="klein leise" style={{ marginBottom: 4 }}>So geht der Push an {r.firma ?? 'die Firma'} raus (nur Mengen, nie Preise):</div>
            <pre className="pushtext">{vorschau}</pre>
          </div>
          <div className="reihe" style={{ justifyContent: 'flex-end' }}>
            <button className="knopf haupt" disabled={laeuft} onClick={schicken}>{laeuft ? 'schickt …' : `Korrektur an ${r.firma ?? 'die Firma'} schicken (${angehakt.length})`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Eine Rechnung im Detail: Positionen tippen, Befunde lesen, entscheiden.
 *
 * DAS AUFMASS STEHT DANEBEN. Wer eine Menge anzweifelt, will die eigene
 * daneben sehen und nicht erst in einen anderen Bildschirm wechseln müssen.
 */
export function Prueffenster({
  ich, id, zu, fertig,
}: { ich: Anmelder; id: string; zu: () => void; fertig: () => void }) {
  const [d, setD] = useState<any>(null);
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [entscheidung, setEntscheidung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const darfPruefen = darf(ich, 'abrechnung.rechnung');
  /* DAS LEISTUNGSVERZEICHNIS ZUM NACHSCHLAGEN (.408). ORIGINALTEXT MARVIN
     (19.10.2026): „wenn ich dort eine Zeile hinzufügen will ... fehlt mir
     hier ein Dropdown-Menü, ich habe die nicht im Kopf. Man muss schon auf
     den Rahmenvertrag zugreifen." Und: „Auch hier unten beim Ausgeklappten
     wird die Bezeichnung nicht richtig übergeben": der Leser hatte
     „Oberfläche aus" abgeschnitten gelesen. Die OZ ist der Schlüssel; steht
     sie im Verzeichnis, kommt die volle Bezeichnung und die Einheit von dort. */
  const [lv, setLv] = useState<any[] | null>(null);
  useEffect(() => { if (darfPruefen) api.lv().then(setLv).catch(() => setLv([])); }, [darfPruefen]);
  const lvZu = (oz: string) => (lv ?? []).find((p: any) => String(p.oz) === String(oz ?? '').trim()) ?? null;
  const ausLv = (z: Zeile): Zeile => {
    const p = lvZu(z.oz);
    if (!p) return z;
    const alt = String(z.bezeichnung ?? '').trim();
    const voll = String(p.text ?? '');
    const abgeschnitten = !alt || voll.toLowerCase().startsWith(alt.toLowerCase()) || alt.length < 8;
    return { ...z, bezeichnung: abgeschnitten ? voll : z.bezeichnung, einheit: z.einheit?.trim() ? z.einheit : String(p.einheit ?? '') };
  };
  /* Sobald das Verzeichnis da ist, bekommen abgeschnittene Zeilen ihren vollen Text. */
  useEffect(() => { if (lv && lv.length) setZeilen((alt) => alt.map(ausLv)); }, [lv]);

  async function laden() {
    try {
      const r = await api.rechnung(id);
      setD(r);
      setZeilen(r.positionen.length
        ? r.positionen.map((p: any) => ({
            oz: p.oz ?? '', bezeichnung: p.bezeichnung ?? '',
            menge: p.menge == null ? '' : String(p.menge),
            einheit: p.einheit ?? '',
          }))
        : [{ ...LEERZEILE }]);
      setEntscheidung(r.rechnung.entscheidung ?? '');
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [id]);
  useFrisch(laden);

  /* DEN BELEG EINLESEN.
     ZWEIMAL DRÜCKEN ERSETZT. Sind schon Positionen erfasst, antwortet der
     Server mit 409 und sagt, wie viele im Beleg stehen; der zweite Versuch
     geht mit `ueberschreiben` hinaus. **Ein Knopf, der stillschweigend
     überschreibt, löscht irgendwann die Zeilen, die jemand von Hand
     eingetragen hat.** */
  async function belegEinlesen(datei: File, ueberschreiben = false) {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.rechnungBeleg(id, datei, ueberschreiben);
      await laden();
      const teile = [`${e.positionen} `
        + `${e.positionen === 1 ? 'Position' : 'Positionen'} gelesen`];
      if (e.zugeordnet) teile.push(`Auftrag gefunden: ${e.zuordnung}`);
      else if (e.grund) teile.push(e.grund);
      /* WER UND WANN, wenn das Blatt es hergegeben hat. Ein Feld, das sich
         still füllt, sieht später aus wie ein Fehler. */
      if (e.firma_gefunden) teile.push(`Firma erkannt: ${e.firma_gefunden}`);
      if (e.faellig_am) {
        teile.push(`Fällig am ${e.faellig_am.split('-').reverse().join('.')}`
                 + (e.faellig_wie ? ` (${e.faellig_wie})` : ''));
      }
      if (e.unsichere_mengen > 0) {
        teile.push(`${e.unsichere_mengen} Menge(n) ohne klare Spalte - bitte `
                 + 'nachsehen');
      }
      /* WORÜBER SIE GEFUNDEN WURDEN, wenn nicht über unsere Nummer. Das ist
         die schwächere Auskunft von beiden und gehört genannt. */
      if (e.ueber_text > 0) {
        teile.push(`${e.ueber_text} davon über die Leistungsbezeichnung, nicht `
                 + 'über unsere Ordnungszahl - bitte gegenlesen');
      }
      /* UND WARUM NICHTS ERKANNT WURDE.
         ==============================================================
         ORIGINALTEXT MARVIN (22.09.2026)
           „und bei dem auslesen der rechnung hast du die positionen auch
            nicht erkannt und abgeglichen gegen unser LEB."

         „0 Positionen gelesen" beantwortet die Frage nicht. **Wer wissen
         will, warum nichts erkannt wurde, muss die Nummern sehen, die
         stattdessen dastehen.** Dann ist die nächste Handlung klar. */
      if (e.positionen === 0 && (e.fremde_nummern ?? []).length) {
        teile.push('Auf dem Papier stehen Nummern, die unser '
                 + 'Leistungskatalog nicht kennt: '
                 + e.fremde_nummern.join(', '));
      }
      setHinweis(teile.join('. ') + '.');
      fertig();
    } catch (f: any) {
      /* DER EINE FEHLER, DER EINE FRAGE IST UND KEIN FEHLER. */
      if (f?.daten?.feld === 'ueberschreiben'
          && await fragen(`${f.message} ${f.daten.hinweis ?? ''}`)) {
        setLaeuft(false);
        return belegEinlesen(datei, true);
      }
      setFehler(ausFehler(f));
    } finally { setLaeuft(false); }
  }

  /* Ein Satz, den der Schritt selbst bestimmt (.480), statt des festen `sagt`. */
  let nachSatz: string | null = null;
  const setHinweisNach = (t: string) => { nachSatz = t; };
  async function tu(was: () => Promise<any>, sagt?: string) {
    setLaeuft(true); setFehler(null); setHinweis(null); nachSatz = null;
    try {
      await was();
      await laden();
      if (nachSatz ?? sagt) setHinweis(nachSatz ?? sagt ?? null);
      fertig();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  if (!d) {
    return (
      <Dialog titel="Rechnung prüfen" breite={980} zu={zu}>
        <Fehlerkasten lage={fehler} />
        {!fehler && <p className="klein leise">Wird geladen.</p>}
      </Dialog>
    );
  }

  const r = d.rechnung;
  const harte = d.befunde.filter((b: any) => b.hart).length;
  const adresse = r.strasse
    ? `${r.strasse} ${r.hausnummer ?? ''}, ${r.plz ?? ''} ${r.ort_text ?? ''}`
    : 'kein Auftrag zugeordnet';

  return (
    <Dialog
      titel={`Rechnung ${r.rechnungsnummer ?? 'ohne Nummer'}`}
      untertitel={`${r.firma ?? 'ohne Firma'}, ${adresse}`}
      merker="nur intern"
      breite={1000} laeuft={laeuft} zu={zu}
    >
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis">{hinweis}</div>}
      <div className="reihe" style={{ marginBottom: 10, gap: 10 }}>
        <NummernKopieren kls={r.kls_id} sm={r.sm_nummer} />
        {r.datei_id && (
          <a className="knopf klein" target="_blank" rel="noreferrer"
             href={api.rechnungAdresse(id, r.dateiname)}>Rechnung ansehen (PDF)</a>
        )}
        {/* IN DEN AUFTRAG (.477). Marvin, 23.10.2026: „neben der Rechnung öffnen auch die
            Rotberichtigung eröffnen lassen, beziehungsweise in den Auftrag reinspringen können."
            Der Weg über das # öffnet die Akte (seit .476 folgt die Oberfläche ihm), das Fenster geht dabei zu. */}
        {/* DIE DATEI, NICHT DER REITER (.522). Marvin, 28.10.2026: „dann soll er auch die
            Datei öffnen und nicht in den Bereich Rotberichtigung reinspringen." Daneben die
            Nachweise (Privatgrund, Mehrtiefe ...), damit sich Abgerechnetes mit dem Beleg
            vergleichen lässt. */}
        {d.unterlagen?.rotberichtigung?.bezug_id
          ? <a className="knopf klein" target="_blank" rel="noreferrer" href={api.dateiAdresse(d.unterlagen.rotberichtigung.bezug_id, d.unterlagen.rotberichtigung.dateiname)} title={`Rotberichtigung vom ${datum(d.unterlagen.rotberichtigung.erstellt_am)} als PDF`}>Rotberichtigung (PDF)</a>
          : r.auftrag_id && <a className="knopf klein" href={`#/akte/${r.auftrag_id}/rb`} title="Noch keine Rotberichtigung erzeugt; öffnet den Reiter in der Akte">Rotberichtigung erzeugen</a>}
        {(d.unterlagen?.nachweise ?? []).map((n: any) => (
          <a key={n.bezug_id} className="knopf klein" target="_blank" rel="noreferrer" href={api.dateiAdresse(n.bezug_id, n.dateiname)} title={n.dateiname}>Nachweis {nachweisTitel(n.dateiname)}</a>
        ))}
        {r.auftrag_id && <a className="knopf klein" href={`#/akte/${r.auftrag_id}`} title="Akte des Auftrags öffnen">Auftrag öffnen</a>}
      </div>
      {/* EINE KLS AN ZWEI AUFTRÄGEN (.414). ORIGINALTEXT MARVIN (19.10.2026,
          RE26045): „Jetzt wurde die Rechnung, weil die SM-Nummer fehlt, der
          falschen, dem Originalauftrag zugeordnet und nicht dem der
          Mängelbeseitigung. Wie kann ich das denn hier jetzt die Rechnung
          anders zuordnen?" Hier stehen die Geschwister mit derselben KLS;
          „hierhin" hängt die Rechnung um, die Gegenprüfung rechnet dann gegen
          die Gutschrift des anderen Auftrags. Rot, wenn die Rechnung ohne
          SM-Nummer kam und der andere Auftrag die offene Anforderung trägt. */}
      {(d.geschwister ?? []).length > 0 && darfPruefen && !r.geklaert_am && (() => {
        const hier = r.auftrag_id;
        const offenAnderswo = (d.geschwister as any[]).filter((g) => g.angefordert_am && !g.rechnung_da);
        const warnen = !r.sm_nummer_papier && offenAnderswo.length > 0 && !d.anforderung;
        return (
          <div className={`karte ${warnen ? 'dublette' : ''}`} style={{ marginBottom: 14 }}>
            <b>KLS {r.kls_id} steht an {d.geschwister.length + 1} Aufträgen</b>
            <p className="klein leise" style={{ margin: '4px 0 8px' }}>
              Zugeordnet ist {r.auftragstyp_mds ?? 'dieser Auftrag'}{r.maengel ? ' (Mängelbeseitigung)' : ''}{d.anforderung ? `, hier angefordert ${datum(d.anforderung.freigegeben_am)}` : ', hier nie angefordert'}.
              {warnen ? ' Ohne SM-Nummer auf dem Blatt kann die Plattform nur raten; der andere Auftrag trägt die offene Anforderung an die Firma.' : ''}
              {' '}Gehört sie woandershin: „hierhin" hängt sie um, die Gegenprüfung läuft dann gegen die Gutschrift des anderen Auftrags.
            </p>
            <table className="liste" style={{ marginTop: 4 }}>
              <thead><tr><th>Auftrag</th><th>SM</th><th>Angefordert</th><th>Rechnung der Firma</th><th></th></tr></thead>
              <tbody>
                {(d.geschwister as any[]).map((g) => (
                  <tr key={g.id}>
                    <td>{g.auftragstyp_mds ?? 'Auftrag'}{g.maengel ? ', Mängelbeseitigung' : ''}{g.archiviert ? ', archiviert' : ''}
                      <div className="klein leise">{`${g.strasse ?? ''} ${g.hausnummer ?? ''}${g.hausnummer_zusatz ?? ''}`.trim()}</div></td>
                    <td className="kennung">{g.sm_nummer ?? ''}</td>
                    <td>{g.angefordert_am ? datum(g.angefordert_am) : <span className="leise">nie</span>}</td>
                    <td>{g.rechnung_da ? 'liegt schon vor' : <span className="leise">keine</span>}</td>
                    <td><button type="button" className={`knopf klein ${g.angefordert_am && !g.rechnung_da ? 'haupt' : ''}`} disabled={laeuft || g.id === hier}
                                onClick={async () => { if (await fragen(`Rechnung ${r.rechnungsnummer ?? ''} dem Auftrag ${g.auftragstyp_mds ?? ''}${g.maengel ? ' (Mängelbeseitigung)' : ''} an ${g.strasse ?? ''} ${g.hausnummer ?? ''} zuordnen?`)) tu(() => api.rechnungAendern(id, { auftrag_id: g.id }), 'Rechnung umgehängt.'); }}>hierhin</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
      {/* ZWEI FASSUNGEN DERSELBEN NUMMER (.408). ORIGINALTEXT MARVIN
          (19.10.2026, RE1295): „Zu dieser Rechnung gab es ein neueres Update,
          eine neuere Datei. Warum ist diese Version nicht bei uns erfasst?"
          Jede Ankunft mit anderem Inhalt steht hier mit PDF; eine wird
          übernommen (Datum, Betrag, Datei, Positionen neu gelesen), die
          bisherige bleibt als Fassung stehen. */}
      {/* DIE RECHNUNGSKORREKTUR DER FIRMA (.448). ORIGINALTEXT MARVIN
          (21.10.2026): „Die haben das halt nochmal als Korrektur geschickt.
          Und die müssen ja jetzt ausgetauscht werden ... Ich kann das im
          System nicht finden." */}
      {(d.korrekturen_firma ?? []).length > 0 && (
        <div className="karte" style={{ marginBottom: 14 }}>
          <b>Rechnungskorrektur der Firma zu dieser Rechnung</b>
          <table className="liste" style={{ marginTop: 6 }}>
            <thead><tr><th>Korrektur</th><th>vom</th><th>Wirkung</th>{d.darfGeld && <th className="r">gutgeschrieben</th>}{d.darfGeld && <th className="r">Betrag vorher</th>}<th></th></tr></thead>
            <tbody>
              {d.korrekturen_firma.map((k: any) => (
                <tr key={k.id}>
                  <td className="kennung">{k.nummer ?? <span className="leise">ohne Nummer</span>}</td>
                  <td>{k.datum ? datum(k.datum) : datum(k.eingegangen_am)}</td>
                  <td>{({ aufgehoben: 'ganz gutgeschrieben, Rechnung aufgehoben', gemindert: 'Teil gutgeschrieben, Betrag gemindert', unklar: 'Betrag nicht eindeutig, bitte ansehen', vermerkt: 'war schon bezahlt, mit nächster Zahlung verrechnen', ersetzt: 'ist jetzt diese Rechnung (neue Fassung)', abgelegt: 'abgelegt, nicht verarbeitet (neue Rechnung erwartet)', fassung: 'als Fassung abgelegt, Rechnung war schon freigegeben' } as Record<string, string>)[k.wirkung] ?? k.wirkung}</td>
                  {d.darfGeld && <td className="r">{k.betrag != null ? euro(k.betrag) : ''}</td>}
                  {d.darfGeld && <td className="r">{k.betrag_vorher != null ? euro(k.betrag_vorher) : ''}</td>}
                  <td>{k.datei && <a className="knopf klein" target="_blank" rel="noreferrer" href={api.rechnungKorrekturAdresse(id, k.id)}>PDF</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(d.fassungen ?? []).length > 0 && (
        <div className={`karte ${d.fassungen.some((f: any) => !f.uebernommen_am) ? 'dublette' : ''}`} style={{ marginBottom: 14 }}>
          <b>Diese Nummer kam in {d.fassungen.length + 1} Fassungen</b>
          <p className="klein leise" style={{ margin: '4px 0 8px' }}>
            Gültig ist, was oben in der Rechnung steht ({r.rechnungsdatum ? datum(r.rechnungsdatum) : 'ohne Datum'}{d.darfGeld && r.betrag != null ? `, ${euro(r.betrag)}` : ''}, {r.dateiname ?? 'ohne Datei'}).
            Die anderen Fassungen stehen hier; „übernehmen" macht eine davon zur gültigen und liest ihre Positionen neu.
          </p>
          <table className="liste" style={{ marginTop: 4 }}>
            <thead><tr><th>Fassung</th><th>Rechnungsdatum</th>{d.darfGeld && <th className="r">Betrag</th>}<th>eingegangen</th><th></th></tr></thead>
            <tbody>
              {d.fassungen.map((f: any) => (
                <tr key={f.id}>
                  <td>{f.dateiname ?? <span className="leise">ohne Datei</span>}{f.uebernommen_am && <span className="marke-zustand z-erledigt" style={{ marginLeft: 6 }}>übernommen {datum(f.uebernommen_am)}</span>}</td>
                  <td>{f.rechnungsdatum ? datum(f.rechnungsdatum) : ''}</td>
                  {d.darfGeld && <td className="r">{f.betrag != null ? euro(f.betrag) : ''}</td>}
                  <td>{datum(f.eingegangen_am)}</td>
                  <td className="reihe" style={{ gap: 6 }}>
                    {f.datei && <a className="knopf klein" target="_blank" rel="noreferrer" href={api.rechnungFassungAdresse(id, f.id)}>PDF</a>}
                    {darfPruefen && !f.uebernommen_am && !r.bezahlt_am && (
                      <button type="button" className="knopf klein" disabled={laeuft}
                              onClick={() => tu(async () => { const e = await api.rechnungFassungUebernehmen(id, f.id); return e; }, 'Fassung übernommen, die Positionen sind neu gelesen.')}>
                        Diese Fassung übernehmen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* DIE GUTSCHRIFT ZUM AUFTRAG, ZUM GEGENPRÜFEN (.408). ORIGINALTEXT
          MARVIN (19.10.2026): „dann fehlt mir hier die Gutschrift, dass ich
          sehen kann, was abgerechnet wurde, um gegenzuprüfen." Nummer, Beleg,
          Datum, Positionen, Betrag nur mit abrechnung.geld, und die Datei. */}
      {(d.gutschriften ?? []).length > 0 ? (
        <div className="karte" style={{ marginBottom: 14 }}>
          <b>Gutschrift der Telekom zu diesem Auftrag</b>
          <table className="liste" style={{ marginTop: 6 }}>
            <thead><tr><th>Gutschrift</th><th>Beleg (LEB)</th><th>Datum</th><th>Positionen</th>{d.darfGeld && <th className="r">Betrag</th>}<th></th></tr></thead>
            <tbody>
              {d.gutschriften.map((g: any) => (
                <tr key={g.id}>
                  <td className="kennung">{g.nummer ?? <span className="leise">ohne Nummer</span>}{g.geprueft_am && <span className="marke-zustand z-erledigt" style={{ marginLeft: 6 }}>geprüft</span>}</td>
                  <td className="kennung">{g.belegnummer ?? ''}</td>
                  <td>{g.datum ? datum(g.datum) : (g.eingegangen_am ? datum(g.eingegangen_am) : '')}</td>
                  <td>{g.positionen}</td>
                  {d.darfGeld && <td className="r">{g.betrag != null ? euro(g.betrag) : ''}</td>}
                  <td>{g.datei_id && <a className="knopf klein" target="_blank" rel="noreferrer" href={api.gutschriftAdresse(g.id)}>Gutschrift ansehen (PDF)</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : r.auftrag_id ? (
        <div className="hinweis" style={{ marginBottom: 14 }}>Zu diesem Auftrag liegt noch keine Gutschrift der Telekom vor.</div>
      ) : null}

      {/* ======================================================================
          DIESELBE NUMMER ZWEIMAL: BEIDE NEBENEINANDER, EINE LÖSCHEN (.230)
          ======================================================================
          ORIGINALTEXT MARVIN (25.09.2026)
            „wenn ich jetzt wie hier sehe rechnung doppelt, dann kann das sein,
             aber ich komme beim klick nicht dahin wo sie liegt, ich muss mir
             das ja manuell ansehen, und ggf. eine löschen. das er nur eine
             behält. das muss das system sich natürlich merken"
          Jede der beiden mit Auftrag, Eingang, Positionen und ihrem PDF. Die
          gelöschte merkt sich der Server (Dateiname und Inhalt), damit der
          Posteingang sie nicht wieder anlegt. */}
      {(d.doppelte ?? []).length > 1 && (
        <div className="karte dublette" style={{ marginBottom: 14 }}>
          <b>Dieselbe Nummer {r.rechnungsnummer} steht {d.doppelte.length}-mal bei {r.firma ?? 'dieser Firma'}</b>
          <p className="klein leise" style={{ margin: '4px 0 8px' }}>
            Beide ansehen. Ist es dasselbe Papier zweimal, eine löschen: die andere bleibt, und kommt
            die gelöschte noch einmal herein, legt die Plattform sie nicht wieder an. Hat die Firma die
            Nummer wirklich zweimal vergeben, beide behalten.
          </p>
          <div className="dublettenreihe">
            {d.doppelte.map((x: any) => (
              <div key={x.id} className={'dublettenkarte' + (x.diese ? ' diese' : '')}>
                <div className="reihe" style={{ justifyContent: 'space-between' }}>
                  <b>{x.diese ? 'Diese Rechnung' : 'Die andere'}</b>
                  {x.bezahlt_am && <span className="merker gruen">bezahlt</span>}
                  {x.geprueft && !x.bezahlt_am && <span className="merker blau">geprüft</span>}
                </div>
                <div className="klein">
                  {x.strasse ? `${x.strasse} ${x.hausnummer ?? ''}, ${x.plz ?? ''} ${x.ort_text ?? ''}` : 'kein Auftrag'}
                  {x.kls_id ? `, KLS ${x.kls_id}` : ''}
                </div>
                <div className="klein leise">
                  Eingang {x.eingang_am ? String(x.eingang_am).slice(0, 10).split('-').reverse().join('.') : '?'}
                  {x.rechnungsdatum ? `, vom ${String(x.rechnungsdatum).slice(0, 10).split('-').reverse().join('.')}` : ''}
                  {`, ${x.positionen} ${x.positionen === 1 ? 'Position' : 'Positionen'}`}
                  {x.betrag != null ? `, ${Number(x.betrag).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}` : ''}
                </div>
                <div className="klein leise" style={{ overflowWrap: 'anywhere' }}>{x.dateiname ?? 'ohne Datei'}</div>
                <div className="reihe" style={{ marginTop: 6, gap: 6 }}>
                  {x.datei_da && (
                    <a className="knopf klein" target="_blank" rel="noreferrer"
                       href={api.rechnungAdresse(x.id, x.dateiname)}>PDF ansehen</a>
                  )}
                  {darfPruefen && !x.bezahlt_am && (
                    <button className="knopf klein gefahr" disabled={laeuft}
                            onClick={async () => {
                              const g = await eingeben(
                                'Diese Rechnung als Dublette löschen? Die andere bleibt.\n\nGrund (optional):',
                                'dasselbe Papier zweimal');
                              if (g === null) return;
                              tu(async () => {
                                const e = await api.rechnungLoeschen(x.id, g);
                                setHinweis(e.klartext);
                                if (x.diese) zu();
                              });
                            }}>
                      Diese löschen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======================================================================
          DER NEUE POSITIONSVERGLEICH STEHT OBEN.
          ======================================================================
          ORIGINALTEXT MARVIN (23.09.2026)
            „baue den neuen postionsvergleich ein, damit ich sauber prüfen
             kann."
          Entwurf C („Entscheiden"): oben das Urteil, darunter nur die
          Abweichungen mit Rechnung, anerkannt und eingereicht und der
          Entscheidung je Position. Die alte Tabelle mit 3 Spalten ist weg;
          das Erfassen von Hand ist aufklappbar darunter. */}
      {/* GEGENPRÜFUNG GEGEN DIE ANFORDERUNG (.358): angefordert, Rechnung,
          Gutschrift und ob das Aufmaß angepasst ist, ohne die PDF zu öffnen. */}
      <Gegenpruefung d={d} id={id} darfPruefen={darfPruefen && !r.geklaert_am}
                     fertig={() => { laden(); fertig(); }} />

      {/* --- Was das System dagegenhält --- */}
      <div className="karte" style={{ marginTop: 14 }}>
        <b>Weitere Prüfungen</b>
        {d.befunde.length === 0 ? (
          <p className="klein leise" style={{ marginBottom: 0 }}>
            {r.positionszeilen === 0
              ? 'Es sind noch keine Positionen erfasst. Ohne Positionen lassen '
                + 'sich nur 3 der 7 Fragen stellen, und die sind ohne '
                + 'Befund.'
              : 'Nichts gefunden. Positionen, Mengen, Zuständigkeit, Zeitraum '
                + 'und Doppelabrechnung passen zusammen.'}
          </p>
        ) : (
          <ul className="klein" style={{ margin: '8px 0 0 0', paddingLeft: 18 }}>
            {d.befunde.map((b: any, i: number) => (
              <li key={i} style={{ marginBottom: 4 }}>
                <span className={'marke-zustand '
                  + (b.hart ? 'z-ueberfaellig' : 'z-neu')}>
                  {BEFUNDWORT[b.art] ?? b.art}
                </span>{' '}
                {b.text}
              </li>
            ))}
          </ul>
        )}
        <p className="klein leise" style={{ marginTop: 10, marginBottom: 0 }}>
          Geprüft wird ohne Preis: Position, Menge, Zuständigkeit, Zeitraum,
          Doppelabrechnung. Der Betrag auf dem Papier wird nicht nachgerechnet,
          er steht nur für die Zahlungserinnerung da.
        </p>
      </div>


      {/* DAS ERFASSEN VON HAND IST AUFKLAPPBAR. Es wird gebraucht, wenn der
          Leser eine Position nicht erkannt hat - nicht bei jeder Prüfung. */}
      <details className="karte positionseditor" style={{ marginTop: 14 }}
               open={r.positionszeilen === 0}>
        <summary><b>Positionen der Rechnung erfassen oder berichtigen ({d.positionen.length})</b></summary>
        <div>
          <div className="reihe" style={{ justifyContent: 'space-between' }}>
            <b>Was auf der Rechnung steht</b>
            {/* HIER STAND „aus dem Blattfeld übernehmen". Der Knopf las
                ein Rohfeld aus der Übernahme und hätte dabei die von
                Hand erfassten Zeilen überschrieben. Migration 069 hat die
                Übernahme einmal gemacht und das Feld abgeräumt.

                AN SEINER STELLE STEHT SEIT .176 DER BELEG SELBST.
                Marvin, 21.09.2026: „er hat nur die daten auf der rechnung
                nicht eingelesen." Die Rechnungen aus der Übernahme hatten
                nie ein Papier in diesem Haus - das Blatt kannte nur Firma,
                Nummer, Betrag und Fälligkeit. **Was es nicht gibt, kann man
                nachreichen**: die PDF liegt auf der QNAP, und wer sie hier
                hineinzieht, bekommt Auftrag, Nummer, Datum, Betrag und
                Positionen in einem Schritt. */}
            {darfPruefen && (
              <label className="knopf klein"
                     style={{ cursor: laeuft ? 'progress' : 'pointer' }}>
                {laeuft ? 'wird gelesen …' : 'Beleg einlesen (PDF)'}
                <input type="file" accept="application/pdf,.pdf"
                       style={{ display: 'none' }} disabled={laeuft}
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         e.target.value = '';
                         if (f) belegEinlesen(f);
                       }} />
              </label>
            )}
          </div>
          {/* ================================================================
              KEIN SCHIEBEBALKEN IN EINEM FENSTER.
              ================================================================
              ORIGINALTEXT MARVIN (22.09.2026)
                „außerdem passe die darstellung an, denn dieser scrollbalken
                 im popup ist unnötig."

              Hier stand ein `.rollt` um die Tabelle und `minWidth: 520` an
              ihr. Die linke Spalte des Fensters ist schmaler als das - also
              stand unter 5 Eingabefeldern ein waagerechter Balken, dauerhaft.
              **Ein Schiebebalken ist die Antwort auf einen Inhalt, der
              wirklich breiter ist als sein Platz; 5 Felder sind das nicht.**

              `tableLayout: fixed` UND PROZENTE STATT PIXEL. Damit teilen sich
              die Spalten den Platz, den es gibt, statt einen zu verlangen,
              den es nicht gibt. Die Eingabefelder darin stehen auf 100 % und
              schrumpfen mit. */}
          <table className="liste"
                 style={{ marginTop: 8, width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {/* SCHMALE ZAHLENSPALTEN, BREITE BEZEICHNUNG (.252). Marvin:
                    „hier sind die spalten zu groß der text ist nicht
                    ausgeschrieben." Eine OZ hat 8 Ziffern, eine Menge selten
                    mehr als 4; der Platz gehört dem Text. */}
                {/* DIE OZ BRAUCHT 8 ZIFFERN PLATZ (.408), Marvin: „das Feld
                    etwas schmäler, damit für die OZ mehr Platz ist". */}
                <th style={{ width: 128 }}>OZ</th>
                <th>Bezeichnung</th>
                <th className="zahl" style={{ width: 76 }}>Menge</th>
                <th style={{ width: 84 }}>Einh.</th>
                <th style={{ width: 34 }}></th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z, i) => (
                <tr key={i} style={{ cursor: 'default' }}>
                  <td>
                    {/* OZ MIT AUSWAHL AUS DEM VERZEICHNIS (.408): tippen oder
                        aus der Liste wählen, die Bezeichnung und die Einheit
                        kommen dann vom Rahmenvertrag. */}
                    <input value={z.oz} disabled={!darfPruefen} list="lv-oz-liste"
                      style={{ width: '100%' }} placeholder="OZ wählen"
                      title={lvZu(z.oz)?.text ?? 'Ordnungszahl aus dem Leistungsverzeichnis'}
                      onChange={(e) => setZeilen(zeilen.map((x, j) =>
                        j === i ? ausLv({ ...x, oz: e.target.value, bezeichnung: lvZu(e.target.value) ? '' : x.bezeichnung, einheit: lvZu(e.target.value) ? '' : x.einheit }) : x))} />
                  </td>
                  <td>
                    <input value={z.bezeichnung} disabled={!darfPruefen} title={lvZu(z.oz)?.text ?? z.bezeichnung}
                      style={{ width: '100%' }}
                      onChange={(e) => setZeilen(zeilen.map((x, j) =>
                        j === i ? { ...x, bezeichnung: e.target.value } : x))} />
                    {lvZu(z.oz) && lvZu(z.oz).text !== z.bezeichnung && (
                      <div className="klein leise" title="Laut Leistungsverzeichnis">
                        LV: {lvZu(z.oz).text}{' '}
                        <button type="button" className="alslink" disabled={!darfPruefen}
                                onClick={() => setZeilen(zeilen.map((x, j) => j === i ? { ...x, bezeichnung: lvZu(z.oz).text, einheit: x.einheit?.trim() ? x.einheit : String(lvZu(z.oz).einheit ?? '') } : x))}>übernehmen</button>
                      </div>
                    )}
                    {z.oz.trim() && lv && lv.length > 0 && !lvZu(z.oz) && (
                      <div className="klein" style={{ color: 'var(--rot, #b3261e)' }}>OZ nicht im Leistungsverzeichnis</div>
                    )}
                  </td>
                  <td>
                    <input value={z.menge} disabled={!darfPruefen}
                      style={{ width: '100%' }}
                      onChange={(e) => setZeilen(zeilen.map((x, j) =>
                        j === i ? { ...x, menge: e.target.value } : x))} />
                  </td>
                  <td>
                    <input value={z.einheit} disabled={!darfPruefen}
                      style={{ width: '100%' }}
                      onChange={(e) => setZeilen(zeilen.map((x, j) =>
                        j === i ? { ...x, einheit: e.target.value } : x))} />
                  </td>
                  <td>
                    <button className="knopf klein" disabled={!darfPruefen}
                      title="Zeile entfernen"
                      onClick={() => setZeilen(zeilen.filter((_, j) => j !== i))}>
                      x
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* DIE LISTE FÜR DIE OZ-FELDER: eine datalist, die der Browser als
              Aufklappmenü zeigt; getippt wird nach OZ oder nach Wort. */}
          {lv && lv.length > 0 && (
            <datalist id="lv-oz-liste">
              {lv.map((p: any) => <option key={p.oz} value={p.oz}>{`${p.text}${p.einheit ? ` (${p.einheit})` : ''}`}</option>)}
            </datalist>
          )}
          <div className="reihe" style={{ marginTop: 8 }}>
            <button className="knopf klein" disabled={!darfPruefen}
              onClick={() => setZeilen([...zeilen, { ...LEERZEILE }])}>
              Zeile hinzufügen
            </button>
            <button className="knopf haupt" disabled={laeuft || !darfPruefen}
              onClick={() => tu(
                () => api.rechnungPositionen(id, zeilen.filter((z) => z.oz.trim())),
                'Die Positionen stehen. Die Befunde sind neu gerechnet.')}>
              Positionen speichern
            </button>
          </div>
          <p className="klein leise" style={{ marginTop: 8, marginBottom: 0 }}>
            Kein Preis. Die Zeilen dienen dem Abgleich mit unserem Aufmaß,
            nicht der Buchhaltung.
          </p>
        </div>

      </details>


      {/* --- Entscheiden --- */}
      {darfPruefen && !r.geklaert_am && (
        <div className="karte" style={{ marginTop: 14 }}>
          <b>Entscheiden</b>
          <Text name="entscheidung" titel="Was ist damit passiert"
            wert={entscheidung} setze={setEntscheidung} mehrzeilig
            fehler={fehler}
            hinweis={harte > 0
              ? `Diese Rechnung hat ${harte} ${harte === 1 ? 'Befund' : 'Befunde'}. `
                + 'Ohne einen Satz dazu lässt sich der Haken „geprüft" nicht setzen.'
              : 'Freiwillig, solange nichts widerspricht.'} />
          <div className="reihe">
            {!r.geprueft && (
              /* GEPRÜFT UND FREIGEGEBEN IN EINEM (.480). Marvin, 23.10.2026: „Wenn ich sie
                 geprüft habe, sind sie aber noch nicht freigegeben. Doppelte Arbeit." Liegt die
                 Rechnung im Prüfordner, gibt „geprüft" sie gleich frei; der Vermerk ist der Grund.
                 Fehlt mehr als die Gutschrift und steht kein Vermerk da, bleibt sie geprüft liegen. */
              <button className="knopf haupt" disabled={laeuft}
                onClick={() => tu(async () => {
                  const a = await api.rechnungAendern(id, { geprueft: true, entscheidung: entscheidung || null });
                  /* .540: in Korrektur abgehakt: die Korrektur ist damit erledigt, die Antwort sagt es. */
                  const vorweg = a?.korrektur_geschlossen && a?.hinweis ? String(a.hinweis) + ' ' : '';
                  if (r.ablage !== 'pruefung') { if (vorweg) setHinweisNach(vorweg.trim()); return; }
                  try { await api.rechnungFreigeben(id, entscheidung && entscheidung.trim().length >= 5 ? entscheidung.trim() : undefined); setHinweisNach(vorweg + 'Geprüft und freigegeben, sie geht in die Zahlung.'); }
                  catch (f: any) { setHinweisNach(`Als geprüft festgehalten, noch nicht freigegeben: ${String(f?.message ?? f)}`); }
                })}>
                {r.ablage === 'pruefung' ? 'geprüft und freigeben' : 'geprüft'}
              </button>
            )}
            {r.geprueft && !r.bezahlt_am && (
              <button className="knopf" disabled={laeuft}
                onClick={() => tu(() => api.rechnungAendern(id, {
                  bezahlt_am: heuteIso(),
                }), 'Als bezahlt festgehalten.')}>
                bezahlt
              </button>
            )}
            <button className="knopf" disabled={laeuft}
              onClick={() => tu(() => api.rechnungAendern(id, {
                entscheidung: entscheidung || null }), 'Vermerk gespeichert.')}>
              nur den Vermerk speichern
            </button>
            <div className="wachsen" />
            <button className="knopf" disabled={laeuft}
              onClick={() => tu(() => api.rechnungAendern(id, {
                geklaert: true, entscheidung: entscheidung || null }),
                'Erledigt.')}>
              erledigt
            </button>
          </div>
        </div>
      )}
      {r.geklaert_am && (
        <div className="reihe" style={{ marginTop: 12, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <p className="klein leise" style={{ margin: 0 }}>
            Geklärt am {datum(r.geklaert_am)}.
            {r.entscheidung && <> Vermerk: {r.entscheidung}</>}
          </p>
          {/* WIEDER ÖFFNEN (.414). ORIGINALTEXT MARVIN (19.10.2026, RE1308):
              „Ich kann hier nichts mehr korrigieren. Wenn ich das jetzt
              ändern will, ist alles ausgeblendet unten. Ich kann es nicht
              mehr anfassen." Geklärt ist kein Siegel: ein Klick nimmt die
              Klärung zurück, Entscheidung, Korrektur und Positionen sind
              wieder anfassbar. Bezahlt bleibt bezahlt. */}
          {darfPruefen && !r.bezahlt_am && (
            <button type="button" className="knopf klein" disabled={laeuft}
                    onClick={async () => { if (await fragen(`Rechnung ${r.rechnungsnummer ?? ''} wieder öffnen? Sie gilt dann wieder als ungeklärt, Korrektur und Positionen sind wieder anfassbar.`)) tu(() => api.rechnungAendern(id, { geklaert: false }), 'Wieder geöffnet.'); }}>
              Wieder öffnen, korrigieren
            </button>
          )}
        </div>
      )}
    </Dialog>
  );
}


// ============================================================================
// Eine Rechnung erfassen
// ============================================================================

/**
 * Das Formular, das es bis zum 26.08.2026 nicht gab.
 *
 * `POST /rechnungen` steht seit Migration 001 im Schema und seit dem
 * 24.08.2026 als Endpunkt da. Aufgerufen hat ihn keine einzige Zeile der
 * Oberfläche: `api.rechnungAnlegen` war definiert und nirgends benutzt. Ohne
 * die Übernahme von früher wäre die Tabelle für immer leer geblieben.
 */
function Erfassen({ ich, zu, fertig }: {
  ich: Anmelder; zu: () => void; fertig: () => void;
}) {
  /* WER DEN BETRAG NICHT SEHEN DARF, BEKOMMT DAS FELD NICHT. Siehe unten
     beim Feld selbst und im Server (21.09.2026). */
  const darfGeld = darf(ich, 'abrechnung.geld');
  const [firmen, setFirmen] = useState<any[]>([]);
  const [w, setW] = useState<any>({
    subunternehmer_id: '', rechnungsnummer: '', rechnungsdatum: '',
    eingang_am: heuteIso(),
    gewerk_gruppe: '', betrag: '', faellig_am: '', bemerkung: '',
    kls: '',
  });
  const [treffer, setTreffer] = useState<any[]>([]);
  const [auftrag, setAuftrag] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => { api.subunternehmer().then(setFirmen).catch(() => {}); }, []);

  // Der Auftrag wird gesucht, nicht aus einer Liste von 365 gewählt.
  async function suche(t: string) {
    setW({ ...w, kls: t }); setAuftrag(null);
    if (t.trim().length < 3) { setTreffer([]); return; }
    try {
      const p = new URLSearchParams({ suche: t.trim(), proSeite: '8' });
      const r = await api.auftraege(p);
      setTreffer(r.zeilen ?? []);
    } catch { setTreffer([]); }
  }

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      await api.rechnungAnlegen({
        subunternehmer_id: w.subunternehmer_id || null,
        rechnungsnummer: w.rechnungsnummer || null,
        rechnungsdatum: w.rechnungsdatum || null,
        eingang_am: w.eingang_am || null,
        gewerk_gruppe: w.gewerk_gruppe || null,
        auftrag_id: auftrag?.id ?? null,
        betrag: w.betrag || null,
        faellig_am: w.faellig_am || null,
        bemerkung: w.bemerkung || null,
      });
      fertig(); zu();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Rechnung erfassen" breite={720} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Feldreihe>
        <Auswahl name="subunternehmer_id" titel="Firma" pflicht
          wert={w.subunternehmer_id} fehler={fehler}
          setze={(v) => setW({ ...w, subunternehmer_id: v })}
          optionen={firmen.map((f) => ({ wert: f.id, text: f.firma }))} />
        <Auswahl name="gewerk_gruppe" titel="Gewerk"
          wert={w.gewerk_gruppe} fehler={fehler}
          setze={(v) => setW({ ...w, gewerk_gruppe: v })}
          optionen={GRUPPEN}
          hinweis="Entscheidet, gegen welche Gewerke geprüft wird." />
      </Feldreihe>
      <Feldreihe>
        <Text name="rechnungsnummer" titel="Rechnungsnummer" wert={w.rechnungsnummer}
          fehler={fehler} setze={(v) => setW({ ...w, rechnungsnummer: v })} />
        <Text name="rechnungsdatum" titel="Rechnungsdatum" art="date"
          wert={w.rechnungsdatum} fehler={fehler}
          setze={(v) => setW({ ...w, rechnungsdatum: v })} />
        <Text name="eingang_am" titel="Eingang" art="date"
          wert={w.eingang_am} fehler={fehler}
          setze={(v) => setW({ ...w, eingang_am: v })} />
      </Feldreihe>
      <Feldreihe>
        {/* DAS FELD STEHT NUR DA, WENN ES AUSGEFÜLLT WERDEN DARF.
            Der Server weist einen Betrag ohne `abrechnung.geld` mit 403 ab
            (21.09.2026); ein Feld, das man ausfüllen kann und das danach
            einen Fehler wirft, ist schlimmer als ein fehlendes. */}
        {darfGeld && (
          <Text name="betrag" titel="Betrag in Euro" wert={w.betrag}
            fehler={fehler} setze={(v) => setW({ ...w, betrag: v })}
            hinweis="Die Summe des Papiers, für die Zahlungserinnerung. Kein Preis je Position." />
        )}
        <Text name="faellig_am" titel="fällig am" art="date" wert={w.faellig_am}
          fehler={fehler} setze={(v) => setW({ ...w, faellig_am: v })} />
      </Feldreihe>

      <Text name="kls" titel="Baustelle" wert={w.kls} fehler={fehler}
        setze={suche} platzhalter="KLS-Nummer oder Straße"
        hinweis={auftrag
          ? `Zugeordnet: ${auftrag.strasse} ${auftrag.hausnummer}, ${auftrag.ort_text}`
          : 'Ohne Auftrag lässt sich nichts gegenhalten. Mindestens 3 Zeichen.'} />
      {!auftrag && treffer.length > 0 && (
        <div className="karte" style={{ marginTop: -6, marginBottom: 12 }}>
          {treffer.map((t: any) => (
            <button key={t.id} className="alslink" style={{ display: 'block' }}
              onClick={() => { setAuftrag(t); setTreffer([]); }}>
              {t.strasse} {t.hausnummer}, {t.ort_text}
              {t.kls_id && <span className="leise">, KLS {t.kls_id}</span>}
            </button>
          ))}
        </div>
      )}

      <Text name="bemerkung" titel="Bemerkung" mehrzeilig wert={w.bemerkung}
        fehler={fehler} setze={(v) => setW({ ...w, bemerkung: v })} />

      <div className="reihe">
        <button className="knopf haupt" disabled={laeuft || !w.subunternehmer_id}
          onClick={speichern}>
          Rechnung erfassen
        </button>
        <button className="knopf" disabled={laeuft} onClick={zu}>Abbrechen</button>
      </div>
      <p className="klein leise" style={{ marginTop: 10, marginBottom: 0 }}>
        Die Positionen werden danach im Prüffenster erfasst. Ohne sie lassen
        sich 6 der 7 Prüfungen nicht stellen.
      </p>
    </Dialog>
  );
}


// ============================================================================
// Die Liste
// ============================================================================

export type Listenfilter = 'leb' | 'ohneleb' | 'befund' | 'fremd' | 'alle' | 'offen' | 'ueberfaellig' | 'ohnepos';

export function Rechnungspruefung({
  ich, oeffne, vorgabe, suche = '',
}: { ich: Anmelder; oeffne: (id: string, reiter?: string) => void;
     /** Die Suche der Seite (.300): KLS-ID, SM, Anschrift, über alle Zustände. */
     suche?: string;
     /** Von den Kacheln oben gesetzt (.260). */
     vorgabe?: { filter: Listenfilter; mal: number } | null }) {
  /* DIE LISTE ÖFFNET MIT „MIT LEB". Marvin, 22.09.2026: „nur rechnungen
     anzeigen die ab der neuen plattform mit den hinterlegten lebs drin
     sind." Warum, steht am Weg `GET /rechnungen`. */
  const [zustand, setZustand] = useState<Listenfilter>('leb');
  const oben = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!vorgabe) return;
    setZustand(vorgabe.filter);
    oben.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [vorgabe?.mal]);
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [pruefe, setPruefe] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [liest, setLiest] = useState(false);
  const [nachgelesen, setNachgelesen] = useState<string | null>(null);
  const darfPruefen = darf(ich, 'abrechnung.rechnung');
  /* DIE SUCHE (.300): KLS-ID, SM, Anschrift, Rechnungsnummer, Firma - über
     ALLE Rechnungen, auch freigegebene und bezahlte. */
  const sucheV = useVerzoegert(suche);

  async function laden() { setD(await api.rechnungen(zustand, sucheV)); }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, [zustand, sucheV]);
  useFrisch(laden);

  /* DAS ERGEBNIS STEHT DA, UND ZWAR IN ZAHLEN. Ein Knopf, der nur „fertig"
     sagt, lässt offen, ob er etwas getan hat. */
  async function nachlesen(neu = false) {
    /* ALLES NEU EINLESEN ÜBERSCHREIBT DATUM UND FÄLLIGKEIT. Wer eines davon
       von Hand berichtigt hat, verliert es - deshalb die Frage vorher. */
    if (neu && !await fragen(
      'Alle Rechnungen mit PDF noch einmal lesen?\n\nRechnungsdatum und '
      + 'Fälligkeit werden neu von der Rechnung gesetzt, auch wo schon etwas steht. '
      + 'Nummer, Betrag, Firma und Auftrag werden nur ergänzt.')) return;
    setLiest(true); setFehler(null); setNachgelesen(null);
    try {
      const e = await api.rechnungenNachlesen(neu);
      setNachgelesen(e.klartext);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLiest(false); }
  }

  if (!d) return null;

  return (
    <>
      <Fehlerkasten lage={fehler} />
      <OhnePositionen fertig={() => laden().catch(() => {})} />
      <div ref={oben} />
      <Karte
        titel={sucheV.trim() ? 'Rechnungen der Subunternehmer: Suche in allen' : 'Rechnungen der Subunternehmer'}
        rechts={
          <span className="reihe">
            {/* JEDER KNOPF TRÄGT SEINE ZAHL. Eine Ansicht, die still kürzer
                wird, sieht aus wie eine, in der weniger Arbeit liegt - die
                übrigen Belege sind nicht weg, sie stehen hier. */}
            {([['leb', `mit LEB (${d.zahlen.mit_leb})`],
               ['ohneleb', `ohne LEB (${d.zahlen.ohne_leb})`],
               ['offen', `offen (${d.zahlen.offen})`],
               ['ueberfaellig', `überfällig (${d.zahlen.ueberfaellig})`],
               ['befund', `mit Befund (${d.zahlen.mit_befund})`],
               ['ohnepos', `ohne Positionen (${d.zahlen.ohne_positionen})`],
               ['fremd', `Fremdbelege (${d.zahlen.fremd})`],
               ['alle', `alle (${d.zahlen.alle})`]] as const).map(([z, t]) => (
              <button key={z} className={'knopf klein filter'
                + (zustand === z && !sucheV.trim() ? ' gewaehlt' : '')}
                onClick={() => setZustand(z as any)}>
                {t}
              </button>
            ))}
            {/* ============================================================
                DEN BESTAND NACHLESEN.
                ============================================================
                ORIGINALTEXT MARVIN (22.09.2026)
                  „überall fehlen die firmen drin."

                Die Rechnungen sind da, ihre PDF liegen in der Ablage, und
                trotzdem steht bei ihnen keine Firma: sie kamen herein,
                bevor der Briefkopf gelesen wurde. **EINE REGEL, DIE ERST AB
                MORGEN GILT, HOLT DEN BESTAND NIE EIN.** Der Knopf liest
                nach und ergänzt nur, was leer ist. */}
            {darfPruefen && (
              <button className="knopf klein" disabled={liest}
                onClick={() => nachlesen(false)}>
                {liest ? 'liest nach …' : 'Bestand nachlesen'}
              </button>
            )}
            {darfPruefen && (
              <button className="knopf klein" disabled={liest}
                title="Jede Rechnung mit PDF noch einmal lesen; Datum und Fälligkeit kommen neu von der Rechnung."
                onClick={() => nachlesen(true)}>
                Alles neu einlesen
              </button>
            )}
            {darfPruefen && (
              <button className="knopf klein haupt" onClick={() => setNeu(true)}>
                Rechnung erfassen
              </button>
            )}
          </span>
        }
      >
        {nachgelesen && <div className="hinweis">{nachgelesen}</div>}
        <SuchHinweis suche={sucheV} n={d.zeilen.length} />
        <div className="reihe" style={{ marginBottom: 10 }}>
          <span className="klein leise">
            {d.zahlen.offen} offen
            {d.zahlen.ueberfaellig > 0 && (
              <>, <span className="rot">{d.zahlen.ueberfaellig} überfällig</span></>
            )}
            {d.zahlen.mit_befund > 0 && (
              <>, <span className="rot">{d.zahlen.mit_befund} mit Widerspruch</span></>
            )}
            {d.zahlen.ohne_positionen > 0 && (
              <>, {d.zahlen.ohne_positionen} ohne erfasste Positionen</>
            )}
            {d.zahlen.sammel > 0 && (
              <>, {d.zahlen.sammel} Sammelrechnungen</>
            )}
            , sortiert nach Fälligkeit, die fälligste zuerst
          </span>
        </div>
        {!d.zeilen.length ? (
          <Leer text={sucheV.trim() ? 'Keine Rechnung passt zur Suche, in keinem Zustand.' : zustand === 'befund'
            ? 'Keine Rechnung, bei der etwas nicht zusammenpasst.'
            : zustand === 'leb'
              ? 'Keine offene Rechnung zu einem Auftrag mit LEB. Die übrigen '
                + 'stehen unter „ohne LEB".'
              : zustand === 'fremd'
                ? 'Keine Fremdbelege: jeder Beleg stammt von einem '
                  + 'Nachunternehmer.'
                : 'Keine Rechnung erfasst.'} />
        ) : (
          /* ==================================================================
             KARTEN STATT TABELLE, NACH FÄLLIGKEIT GEGLIEDERT (.215).
             ==================================================================
             ORIGINALTEXT MARVIN (23.09.2026)
               „kann man hier die übersicht auch anpassen und attraktiver
                gestalten"
             Dasselbe Muster wie die Gutschriften: vorn groß, was sortiert
             (hier die Fälligkeit), in der Mitte Firma und Baustelle, rechts
             Betrag und Ergebnis der Prüfung. Die linke Kante trägt die Farbe
             des Ergebnisses. Die Gruppen: überfällig, diese Woche, später,
             ohne Fälligkeit. */
          <div className="rk-liste">
            {rechnungsGruppen(d.zeilen).map(([titel, gruppe]) => (
              <section key={titel} className="gs-tag">
                <h3 className="gs-tagkopf">
                  <span>{titel}</span>
                  <span className="klein leise">
                    {gruppe.length} {gruppe.length === 1 ? 'Rechnung' : 'Rechnungen'}
                    {d.darfGeld && `, ${euro(gruppe.reduce((s: number, r: any) => s + (Number(r.betrag) || 0), 0))}`}
                  </span>
                </h3>
                {gruppe.map((r: any) => {
                  const lage = rechnungsLage(r);
                  const t = r.tage_bis_faellig == null ? null : Number(r.tage_bis_faellig);
                  return (
                    <article key={r.id} className={'gs-karte rk-' + lage.art}>
                      {/* DAS DATUM ALLEIN UND DARUNTER NUR DER ABSTAND (.216).
                          Marvin: „hier haben wir erstmal darstellungsfehler".
                          Hier stand die ganze Fälligkeit samt „Zahlungsziel 30
                          Tage ab Rechnungsdatum" in einer schmalen Spalte und
                          lief über die KLS daneben. Woher die Frist kommt,
                          steht jetzt in der Mitte bei den Nummern. */}
                      <div className="gs-datum">
                        {r.faellig_am ? (
                          <>
                            <div className={'gs-datum-gross' + (!r.bezahlt_am && t != null && t < 0 ? ' rot' : '')}>
                              {datum(r.faellig_am).slice(0, 6)}
                            </div>
                            <div className={'rk-abstand klein' + (r.bezahlt_am ? '' : t != null && t < 0 ? ' f-vorbei' : t != null && t <= 7 ? ' f-bald' : ' f-spaeter')}>
                              {r.bezahlt_am ? 'bezahlt'
                                : t == null ? 'fällig'
                                : t < 0 ? `seit ${-t} ${-t === 1 ? 'Tag' : 'Tagen'} fällig`
                                : t === 0 ? 'heute fällig' : t === 1 ? 'morgen fällig' : `fällig in ${t} Tagen`}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="rk-ohne">
                              ohne
                            </div>
                            <div className="klein leise">Fälligkeit</div>
                          </>
                        )}
                      </div>

                      <div className="gs-mitte">
                        <div className="gs-titel">
                          {r.firma ?? <span className="rot">ohne Firma</span>}
                          {(r.firma_gruppe ?? r.gewerk_gruppe) && (
                            <span className="gewerk" style={{ marginLeft: 8 }}>
                              {(r.firma_gruppe ?? r.gewerk_gruppe) === 'tiefbau' ? 'Tiefbau' : 'Montage'}
                            </span>
                          )}
                        </div>
                        {!r.firma && r.lesebefund && (
                          <div className="klein leise lesebefund">{r.lesebefund}</div>
                        )}
                        <div className="klein" style={{ marginTop: 2 }}>
                          {r.auftrag_id ? (
                            <button className="alslink" onClick={() => oeffne(r.auftrag_id)}>
                              {r.strasse
                                ? `${r.strasse} ${r.hausnummer ?? ''}${r.hausnummer_zusatz ?? ''}`.trim()
                                : (r.kls_id ?? 'Auftrag')}
                            </button>
                          ) : Number(r.auftraege) >= 2 ? (
                            <span>{r.strasse
                              ? `${r.strasse} ${r.hausnummer ?? ''}${r.hausnummer_zusatz ?? ''}`.trim()
                              : 'Sammelrechnung'}, Sammelrechnung, {r.auftraege} Aufträge</span>
                          ) : <span className="rot">ohne Auftrag</span>}
                          {r.strasse && <span className="leise">, {r.plz ? r.plz + ' ' : ''}{r.ort_text}</span>}
                        </div>
                        <div className="gs-nummern klein">
                          <NummernKopieren kls={r.kls_id} sm={r.sm_nummer} />
                          <span className="leise">
                            {r.rechnungsnummer ?? 'ohne Nummer'}
                            {r.rechnungsdatum
                              ? <> vom {datum(r.rechnungsdatum)}</>
                              : <span className="rot"> ohne Datum</span>}
                            {', '}Eingang {datum(r.eingang_am)}
                            {r.bezahlt_am && <>, bezahlt {datum(r.bezahlt_am)}</>}
                          </span>
                        </div>
                        {r.faellig_am && (
                          <div className="klein leise">
                            fällig {datum(r.faellig_am)}{r.faellig_wie ? `, ${r.faellig_wie}` : ''}
                          </div>
                        )}
                        {(r.befund_text || r.hinweis_text) && (
                          <div className={'klein rk-befundtext' + (r.befunde_hart > 0 ? ' rot' : '')}>
                            {r.befunde_hart > 0 ? r.befund_text : r.hinweis_text}
                          </div>
                        )}
                      </div>

                      <div className="gs-rechts">
                        {d.darfGeld && (
                          <div className="gs-betrag">
                            {r.betrag != null ? euro(Number(r.betrag)) : '-'}
                          </div>
                        )}
                        <span className={'marke-zustand ' + lage.farbe} title={r.befund_text ?? r.hinweis_text ?? ''}>
                          {lage.text}
                        </span>
                        <div className="reihe" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          {r.geklaert_am && <span className="marke-zustand z-erledigt">geklärt</span>}
                          {/* DER ZUSTAND DER ZAHLUNG UND FREIGABE (.300), damit
                              die Suche über alle sagt, wo eine Rechnung steht. */}
                          {r.abgelehnt_grund && <span className="marke-zustand z-ueberfaellig" title={r.abgelehnt_grund}>abgelehnt</span>}
                          {!r.abgelehnt_grund && r.freigegeben_am && <span className="marke-zustand z-erledigt">freigegeben {datum(r.freigegeben_am)}</span>}
                          {!r.bezahlt_am && r.zahlung_zustand && r.zahlung_zustand !== 'abgebrochen' && <span className="marke-zustand z-neu">in Revolut</span>}
                          {r.bezahlt_am && <span className="marke-zustand z-erledigt">bezahlt {datum(r.bezahlt_am)}</span>}
                          {r.korrektur_offen > 0 && (
                            <span className="marke-zustand z-neu">Korrektur verlangt</span>
                          )}
                          <button className="knopf klein haupt" onClick={() => setPruefe(r.id)}>prüfen</button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </section>
            ))}
          </div>
        )}
        <p className="klein leise" style={{ marginTop: 10, marginBottom: 0 }}>
          Der Betrag ist die Summe des Papiers und dient der
          Zahlungserinnerung. Gegengehalten werden Positionen und Mengen, nicht
          Preise.
        </p>
      </Karte>

      {pruefe && (
        <Prueffenster ich={ich} id={pruefe} zu={() => setPruefe(null)}
                      fertig={() => laden().catch(() => {})} />
      )}
      {neu && (
        <Erfassen ich={ich} zu={() => setNeu(false)}
                  fertig={() => laden().catch(() => {})} />
      )}
    </>
  );
}
